import { inject, Injectable } from '@angular/core';
import { ProtobufService } from '../protobuf.service';
import { firstValueFrom, map, Observable } from 'rxjs';
import {
  GetIntegrationAppListResponse,
  GetIntegrationAppDetailsResponse,
  InstallPbxIntegrationAppRequest,
  InstallPbxIntegrationAppResponse,
  UninstallPbxIntegrationAppRequest,
  UninstallPbxIntegrationAppResponse,
  UninstallPbxIntegrationAppResponse_Outcome,
  CreateIntegrationAppRequest,
  CreateIntegrationAppResponse,
  GetPbxIntegrationAppInstallationsRequest,
  GetPbxIntegrationAppInstallationsResponse,
  IntegrationAppInstallation_State,
  UpgradeIntegrationAppRequest,
  UpgradeIntegrationAppResponse,
} from '@proto/odoo/integrations';
import { ToastService } from '../toast.service';
import { I18NextService } from 'angular-i18next';

@Injectable({
  providedIn: 'root',
})
export class IntegrationAppsService {

  private toastService = inject(ToastService);
  private i18Next = inject(I18NextService);

  private readonly baseUrl = '/services/integration-app';

  constructor(private protoService: ProtobufService) { }
  
  getMarketplaceApps(): Observable<GetIntegrationAppListResponse> {
    const url = `${this.baseUrl}/marketplace`;
    return this.protoService.get(
      url,
    ).pipe(
      map(response => {
        return GetIntegrationAppListResponse.decode(response);
      })
    );
  }

  getPrivateApps(): Observable<GetIntegrationAppListResponse> {
    const url = `${this.baseUrl}/private`;
    return this.protoService.get(
      url,
    ).pipe(
      map(response => {
        return GetIntegrationAppListResponse.decode(response);
      })
    );
  }

  getAppInstallations(req: GetPbxIntegrationAppInstallationsRequest): Observable<GetPbxIntegrationAppInstallationsResponse> {
    const url = `${this.baseUrl}/installations/search`;
    return this.protoService.post(
      url,
      GetPbxIntegrationAppInstallationsRequest.encode(req),
    ).pipe(
      map(response => {
        return GetPbxIntegrationAppInstallationsResponse.decode(response);
      })
    );
  }

  getAppDetails(uuid: string, version?: number): Observable<GetIntegrationAppDetailsResponse> {
    const url = version ? `${this.baseUrl}/${uuid}?version=${version}` : `${this.baseUrl}/${uuid}`;
    return this.protoService.get(
      url
    ).pipe(
      map(response => {
        return GetIntegrationAppDetailsResponse.decode(response);
      })
    );
  }

  installApp(req: InstallPbxIntegrationAppRequest): Observable<InstallPbxIntegrationAppResponse> {
    const url = `${this.baseUrl}/install`;
    return this.protoService.put(
      url,
      InstallPbxIntegrationAppRequest.encode(req),
    ).pipe(
      map(response => {
        return InstallPbxIntegrationAppResponse.decode(response);
      })
    );
  }

  async uninstallAppWithFeedback(applianceId: number, uuid: string): Promise<boolean> {
    const req = UninstallPbxIntegrationAppRequest.create({ applianceId, integrationAppUuid: uuid });
    try {
      const res = await firstValueFrom(this.uninstallApp(req));
      if (res.outcome === UninstallPbxIntegrationAppResponse_Outcome.OUTCOME_SUCCESS) {
        this.toastService.success(this.i18Next.t('integration_apps_toast_uninstallation_success'), 'success');
        return true;
      }
      if (res.outcome === UninstallPbxIntegrationAppResponse_Outcome.OUTCOME_ERROR) {
        this.toastService.error(this.i18Next.t('integration_apps_toast_uninstallation_error'), 'error');
      }
      return false;
    } catch (error) {
      this.toastService.error(this.i18Next.t('integration_apps_start_uninstallation_error'), 'error');
      return false;
    }
  }

  uninstallApp(req: UninstallPbxIntegrationAppRequest): Observable<UninstallPbxIntegrationAppResponse> {
    const url = `${this.baseUrl}/uninstall`;
    return this.protoService.put(
      url,
      UninstallPbxIntegrationAppRequest.encode(req),
    ).pipe(
      map(response => {
        return UninstallPbxIntegrationAppResponse.decode(response);
      })
    );
  }

  createIntegrationApp(req: CreateIntegrationAppRequest): Observable<CreateIntegrationAppResponse> {
    const url = this.baseUrl;
    return this.protoService.put(
      url,
      CreateIntegrationAppRequest.encode(req),
    ).pipe(
      map(response => {
        return CreateIntegrationAppResponse.decode(response);
      })
    );
  }

  createNewIntegrationAppVersion(appId: string, req: UpgradeIntegrationAppRequest): Observable<UpgradeIntegrationAppResponse> {
    const url = `${this.baseUrl}/${appId}/upgrade`;
    return this.protoService.put(
      url,
      UpgradeIntegrationAppRequest.encode(req),
    ).pipe(
      map(response => {
        return UpgradeIntegrationAppResponse.decode(response);
      })
    );
  }

  getInstallationStateLabelKey(state?: IntegrationAppInstallation_State): string {
    switch (Number(state)) {
      case IntegrationAppInstallation_State.STATE_INSTALL_STARTED:
        return 'integration-apps-service_installation_started_state';
      case IntegrationAppInstallation_State.STATE_INSTALL_PROGRESS:
        return 'integration-apps-service_installation_progress_state';
      case IntegrationAppInstallation_State.STATE_INSTALL_FAILED:
        return 'integration-apps-service_installation_failed_state';
      case IntegrationAppInstallation_State.STATE_UNINSTALL_STARTED:
        return 'integration-apps-service_uninstallation_started_state';
      case IntegrationAppInstallation_State.STATE_UNINSTALL_PROGRESS:
        return 'integration-apps-service_uninstallation_progress_state';
      case IntegrationAppInstallation_State.STATE_UNINSTALL_FAILED:
        return 'integration-apps-service_uninstallation_failed_state';
      case IntegrationAppInstallation_State.STATE_INSTALLED:
      case IntegrationAppInstallation_State.STATE_UNINSTALLED:
        return '';
      case IntegrationAppInstallation_State.STATE_UNKNOWN:
      case IntegrationAppInstallation_State.UNRECOGNIZED:
      default:
        return 'integration-apps-service_unknown_state';
    }
  }

  isPendingState(state?: IntegrationAppInstallation_State): boolean {
    return state === IntegrationAppInstallation_State.STATE_INSTALL_STARTED
      || state === IntegrationAppInstallation_State.STATE_INSTALL_PROGRESS
      || state === IntegrationAppInstallation_State.STATE_UNINSTALL_STARTED
      || state === IntegrationAppInstallation_State.STATE_UNINSTALL_PROGRESS;
  }

}